import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CreditCard, Loader2, Plus, Sparkles, Zap } from "lucide-react";
import { toast } from "sonner";
import i18next from "i18next";
import { Trans, useTranslation } from "react-i18next";

import { config } from "../../../Environment";
import { cn } from "../../../lib/utils";
import { currencyAmount } from "../../../lib/currency";
import useRetryFetch from "../../../hooks/useRetryFetch";
import { tryGetUserProfile } from "../../../api/auth";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { PaymentIframe } from "../../payment/PaymentIframe";

// Every studio AI action (draft, suggest-become, suggest-facet, suggest work
// items) consumes Activity Generator Credits. This module makes that visible —
// a live balance chip, an explicit cost, a clear out-of-credits error — and
// lets the user buy more without leaving the studio (activity_generator_credit
// products via the standard PayProGlobal iframe flow).

export const AI_CREDITS_KEY = ["studio-ai-credits"];

/** Fresh available-credit count from the profile endpoint; null while unknown */
export const useAiCredits = (): number | null => {
  const retryFetch = useRetryFetch();
  const { data } = useQuery({
    queryKey: AI_CREDITS_KEY,
    queryFn: async () => (await tryGetUserProfile(retryFetch)) ?? null,
    staleTime: 30_000,
  });
  return data?.availableActivityGeneratorCredits ?? null;
};

/** Returns a callback that refetches the balance — call after any AI request */
export const useRefreshAiCredits = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: AI_CREDITS_KEY });
};

// ── Buy-credits dialog (event-based singleton) ────────────────────────────────
// Chips and toasts can request the dialog from anywhere via openBuyAiCredits();
// exactly one BuyAiCreditsHost is mounted per studio screen and owns the dialog.

const BUY_AI_CREDITS_EVENT = "clarynext:buy-ai-credits";

export const openBuyAiCredits = () =>
  window.dispatchEvent(new Event(BUY_AI_CREDITS_EVENT));

/** Toast for failed AI calls — out-of-credits gets an explicit, actionable message */
export const aiErrorToast = (e: any, cost = 1) => {
  if (e?.status === 402) {
    toast.error(String(i18next.t("pwf.studio.aiCredits.notEnough", { count: cost })), {
      action: {
        label: String(i18next.t("pwf.studio.aiCredits.getCredits")),
        onClick: openBuyAiCredits,
      },
    });
  } else {
    toast.error(e?.message ?? String(i18next.t("pwf.studio.aiCredits.suggestFailed")));
  }
};

interface CreditProduct {
  _id: string;
  name?: string;
  price: { amount: number; currency: string };
  data?: { activity_generator_credit?: { amount?: number } };
}

export const BuyAiCreditsHost: React.FC = () => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(BUY_AI_CREDITS_EVENT, handler);
    return () => window.removeEventListener(BUY_AI_CREDITS_EVENT, handler);
  }, []);
  if (!open) return null;
  return <BuyAiCreditsDialog onClose={() => setOpen(false)} />;
};

const BuyAiCreditsDialog: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const refreshAiCredits = useRefreshAiCredits();
  const credits = useAiCredits();

  const [buyingId, setBuyingId] = useState<string | null>(null);
  const [paymentUrl, setPaymentUrl] = useState("");
  const [showPaymentIframe, setShowPaymentIframe] = useState(false);

  const { data: products, isLoading } = useQuery({
    queryKey: ["ai-credit-products"],
    queryFn: async (): Promise<CreditProduct[]> => {
      const res = await retryFetch(
        `${config.API_BASE}/api/product/searchActive?type=activity_generator_credit`
      );
      if (!res.ok) throw new Error("Could not load credit packages");
      const data = await res.json();
      return Array.isArray(data.list) ? data.list : [];
    },
  });

  const buy = async (product: CreditProduct) => {
    setBuyingId(product._id);
    try {
      const res = await retryFetch(`${config.API_BASE}/api/payment/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ productId: product._id, useIframe: true }),
      });
      if (!res.ok) throw new Error(t("pwf.studio.aiCredits.paymentInitError"));
      const result = await res.json();
      if (result.type === "redirect" && result.redirect) {
        setPaymentUrl(result.redirect);
        setShowPaymentIframe(true);
      } else {
        // Free packages skip the gateway and are credited immediately
        refreshAiCredits();
        toast.success(t("pwf.studio.aiCredits.creditsAdded"));
        onClose();
      }
    } catch (e: any) {
      toast.error(e.message ?? t("pwf.studio.aiCredits.paymentInitError"));
    } finally {
      setBuyingId(null);
    }
  };

  const handlePaymentSuccess = () => {
    setShowPaymentIframe(false);
    refreshAiCredits();
    toast.success(t("pwf.studio.aiCredits.paymentSuccess"));
    onClose();
  };

  return (
    <>
      <Dialog open={!showPaymentIframe} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-500" />
              {t("pwf.studio.aiCredits.dialogTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("pwf.studio.aiCredits.dialogDesc")}
              {credits !== null && (
                <span className="block mt-1 text-slate-600">
                  <Trans
                    i18nKey="pwf.studio.aiCredits.youHave"
                    count={credits}
                    components={{ bold: <strong /> }}
                  />
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 text-amber-400 animate-spin" />
            </div>
          ) : !products || products.length === 0 ? (
            <p className="text-sm text-slate-500 py-4">
              {t("pwf.studio.aiCredits.noPackages")}
            </p>
          ) : (
            <div className="space-y-2">
              {products.map((product) => (
                <button
                  key={product._id}
                  onClick={() => buy(product)}
                  disabled={buyingId !== null}
                  className="w-full flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50/40 hover:bg-amber-50 hover:border-amber-300 p-4 text-start transition-colors disabled:opacity-60"
                >
                  <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    {buyingId === product._id ? (
                      <Loader2 className="h-5 w-5 text-amber-600 animate-spin" />
                    ) : (
                      <Zap className="h-5 w-5 text-amber-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900">
                      {product.data?.activity_generator_credit?.amount != null
                        ? t("pwf.studio.aiCredits.creditsCount", {
                            count: product.data.activity_generator_credit.amount,
                          })
                        : t("pwf.studio.aiCredits.creditsUnknown")}
                    </p>
                    {product.name && (
                      <p className="text-xs text-slate-500 truncate">{product.name}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="font-bold text-amber-700">
                      {currencyAmount(product.price.amount, product.price.currency)}
                    </span>
                    <CreditCard className="h-4 w-4 text-amber-400" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PaymentIframe
        open={showPaymentIframe}
        paymentUrl={paymentUrl}
        onSuccess={handlePaymentSuccess}
        onError={(error) => {
          setShowPaymentIframe(false);
          toast.error(error || t("pwf.studio.aiCredits.paymentFailed"));
        }}
        onClose={() => setShowPaymentIframe(false)}
      />
    </>
  );
};

// ── Balance chip ──────────────────────────────────────────────────────────────

/**
 * "✨ Uses 1 credit · 4 left · Get more" chip; turns red when the balance can't
 * cover the cost. "Get more" opens the buy dialog (requires a BuyAiCreditsHost
 * mounted on the screen).
 */
export const AiCreditsChip: React.FC<{ cost?: number; className?: string }> = ({
  cost = 1,
  className,
}) => {
  const { t } = useTranslation();
  const credits = useAiCredits();
  if (credits === null) return null;
  const short = credits < cost;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] whitespace-nowrap",
        short
          ? "border-red-200 bg-red-50 text-red-600"
          : "border-slate-200 bg-white/80 text-slate-500",
        className
      )}
      title={t("pwf.studio.aiCredits.chipTitle", { count: cost, remaining: credits })}
    >
      <Sparkles className="h-3 w-3" />
      {t("pwf.studio.aiCredits.chipText", { count: cost, remaining: credits })}
      <button
        onClick={openBuyAiCredits}
        className={cn(
          "inline-flex items-center gap-0.5 font-medium hover:underline",
          short ? "text-red-700" : "text-amber-600"
        )}
        title={t("pwf.studio.aiCredits.buyTitle")}
      >
        <Plus className="h-3 w-3" />
        {t("pwf.studio.aiCredits.getMore")}
      </button>
    </span>
  );
};

export const AI_CREDIT_TITLE = "Uses 1 AI credit";
